/**
 * Heading component for the Indigo design system.
 *
 * A semantic heading component that renders the appropriate HTML heading tag (h1-h6)
 * based on the level prop, while applying consistent typography styles from the design system.
 *
 * Features:
 * - Semantic HTML rendering (h1 through h6)
 * - Design system typography styles
 * - Support for Tailwind utility classes via className prop
 * - TypeScript-first with proper prop types
 * - Forwards ref for DOM manipulation
 *
 * @example
 * ```tsx
 * // H1 heading (default)
 * <Heading>Main Page Title</Heading>
 *
 * // H2 heading
 * <Heading level={2}>Section Title</Heading>
 *
 * // With additional Tailwind utilities
 * <Heading level={3} className="text-indigo-primary mb-4">
 *   Custom Styled Heading
 * </Heading>
 * ```
 */
import { cva, type VariantProps } from "class-variance-authority";
import { CSSProperties, forwardRef, HTMLAttributes } from "react";
import "./Typography.css";

const headingVariants = cva("indigo-typography-base", {
  variants: {
    level: {
      1: "indigo-typography-h1",
      2: "indigo-typography-h2",
      3: "indigo-typography-h3",
      4: "indigo-typography-h4",
      5: "indigo-typography-h5",
      6: "indigo-typography-h6",
    },
  },
  defaultVariants: {
    level: 1,
  },
});

export interface HeadingProps
  extends Omit<HTMLAttributes<HTMLHeadingElement>, "level">,
    VariantProps<typeof headingVariants> {
  /**
   * The heading level (1-6) which determines both the HTML tag and styling
   * @default 1
   */
  level?: 1 | 2 | 3 | 4 | 5 | 6;

  /**
   * Additional CSS classes to apply
   */
  className?: string;

  /**
   * Choose between the default Indigo heading font and the Society display font
   * @default "default"
   */
  fontFamily?: "default" | "society";

  /**
   * Optional explicit font size in pixels. Overrides design token sizing when provided.
   */
  fontSizePx?: number;

  /**
   * The content to display in the heading
   */
  children?: React.ReactNode;
}

/**
 * Heading component
 *
 * Renders a semantic HTML heading element with consistent typography styles.
 *
 * @param props - Component props (@see HeadingProps)
 * @returns A React heading component (h1-h6)
 */
export const Heading = forwardRef<HTMLHeadingElement, HeadingProps>(
  (
    {
      level = 1,
      className,
      children,
      fontFamily = "default",
      fontSizePx,
      style,
      ...props
    },
    ref,
  ) => {
    const Tag = `h${level}` as const;
    const headingClass = headingVariants({ level });
    const combinedClassName = className
      ? `${headingClass} ${className}`
      : headingClass;

    const mergedStyle: CSSProperties = {
      ...(style ?? {}),
      ...(fontFamily === "society"
        ? {
            fontFamily: "'Society', var(--indigo-font-body)",
            // Society only ships with the light weight, so force 300 to avoid
            // browser fallbacks when callers request bold headings.
            fontWeight: 300,
          }
        : {}),
    };

    if (typeof fontSizePx === "number") {
      mergedStyle.fontSize = `${fontSizePx}px`;
    }

    const hasStyleOverrides = Object.keys(mergedStyle).length > 0;

    return (
      <Tag
        ref={ref}
        className={combinedClassName}
        style={hasStyleOverrides ? mergedStyle : undefined}
        {...props}
      >
        {children}
      </Tag>
    );
  },
);

Heading.displayName = "Heading";

export { headingVariants };
